// #region Imports
import CommentIcon from "@mui/icons-material/Comment";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupsIcon from "@mui/icons-material/Groups";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import WarningIcon from "@mui/icons-material/Warning";
import Badge from "@mui/material/Badge";
import Link from "@mui/material/Link";
import { ThemeProvider, alpha, styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import EditFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/edit-fee-schedule-category-drawer";
import ExistingFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/existing-fee-schedule-category-drawer";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import { getFeeScheduleTeamMembers, getMarketplaceTeamId } from "_shared/fee-schedules/categories/fee-schedule-team-members";
import Guid from "_shared/guids/guid";
import { chain, uniqBy } from "lodash";
import { useGetTeamByIdQuery } from "marketplace/teams/_api/marketplace-team-api";
import { getTeamById } from "marketplace/teams/_store/marketplace-teams-redux-slice";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useBlocker, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { FeeScheduleCategoryMaskedCurrency, Masked, MaskedTeam } from "work/masked-fields";
import {
  ButtonContainer,
  Content,
  ProposalTab,
  TabContent,
  TabsContainer,
} from "work/_views/work-page-styled-components";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import "work/proposals/__extensions/users/user-extensions";
import {
  useCreateProposalMutation,
  useDeleteProposalMutation,
  useGiveReviewerApprovalMutation,
  useSaveProposalMutation,
} from "work/proposals/_api/proposal-api";
import { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import {
  getProposalIsNoLongerAvailable,
  clearNewProposalBuilder,
  clearLocalProposalById,
  clearProposalBuilderByProposalId,
  getOriginalProposalBuilderByProposalId,
  getUpdatedProposalBuilderByProposalId,
  isProposalLoadIssue,
  promoteCreatedProposalDraft,
  replaceOriginalProposalBuilder,
  replaceUpdatedProposalBuilder,
} from "work/proposals/_store/proposals-redux-slice";
import ProposalSecondaryActions, {
  type ProposalSecondaryAction,
} from "work/proposals/_views/_buttons/proposal-secondary-actions";
import SubmitReviewButton from "work/proposals/_views/_buttons/submit-review-button";
import {
  getProposalOperationDropWarning,
  getProposalSaveErrorMessage,
  getProposalSaveSuccessMessage,
  getSaveConfirmationCopy,
  proposalSaveChangeLogHeading,
} from "work/proposals/_views/_shared/proposal-save-confirmation-copy";
import type { ConfirmationMode } from "work/proposals/_views/_shared/proposal-save-confirmation-copy";
import ProposalChangeSummaryList from "work/proposals/_views/_shared/proposal-change-summary-list";
import {
  buildProposalSaveSharePlan,
  proposalSaveAudienceLabel,
} from "work/proposals/_views/_shared/proposal-change-share-plan";
import { useProposalChangeDescriptionContext } from "work/proposals/_views/_shared/use-proposal-change-description-context";
import type { ProposalChangeDescriptionContext } from "work/proposals/negotiation/proposal-change-description";
import {
  CommentButton,
  CommentButtonLabel,
  HighlightCommentButton,
  IssueTrackerFooter,
  IssueTrackerHeader,
  ProposalActionButton,
  ProposalActions,
  ProposalPageActionButtons as ActionButtonsContainer,
  ProposalPageActionsContainer as PageActionsContainer,
  ProposalPageContainer as PageContainer,
  ProposalPageHeader as HeaderContainer,
  ProposalPageMain as Main,
  ProposalPageTitle as PageTitle,
  ProposalPageTitleBreadcrumb as PageTitleBreadcrumb,
  ProposalPageTitleContainer as TitleContainer,
  ProposalSidePanelColumn as SidePanelColumn,
  ProposalStickyPageHeader as StickyPageHeader,
  ProposalTabsHeader,
  SaveButton,
  SaveButtonLabel,
} from "work/proposals/_views/_shared/proposal-styled-components";
import {
  getProposalDrawerWidth,
  NEW_FEE_CATEGORY_DRAWER_KEY,
  useProposalPageCanSeatSidePanel,
  useProposalPagePanels,
} from "work/proposals/_views/_shared/proposal-page-panels";
import { usePersistedProposalSaveOrchestration } from "work/proposals/_views/_shared/use-proposal-save-orchestration";
import useProposalTabPendingCounts from "work/proposals/_views/_shared/use-proposal-tab-pending-counts";
import { paneFlushContent, paneIsCompact } from "work/_views/work-pane-layout";
import useWorkPaneTheme from "work/_views/work-pane-typography";
import useWorkPaneSize, {
  WorkPaneWidthContext,
} from "work/_views/use-work-pane-size";
import { isProposalNoLongerAvailableError } from "work/proposals/_views/_shared/proposal-availability-error";
import {
  readProposalCommandErrorDetail,
  readProposalConflictResponse,
  useCompleteProposalReviewMutation,
  useLazyGetProposalCollaborationQuery,
  useMarkProposalChangesSeenMutation,
  useSubmitProposalCollaborationDraftMutation,
} from "work/proposals/negotiation/proposal-collaboration-api";
import { describeProposalConflicts } from "work/proposals/negotiation/proposal-conflict-summary";
import {
  buildCompleteProposalReviewCommand,
  buildSubmitProposalDraftCommand,
  isBarredFromInitialProposalSetup,
} from "work/proposals/negotiation/proposal-command-builder";
import {
  proposalEnvelopeReceived,
  proposalOperationsQueued,
} from "work/proposals/negotiation/proposal-collaboration-redux-slice";
import { buildProposalSubmissionRevertOperations } from "work/proposals/negotiation/proposal-submission-revert";
import useSubmitProposalDialog, {
  CANCELED_SUBMISSION,
} from "work/proposals/_views/_shared/use-submit-proposal-dialog";
import { isReviewerApprovalCurrent } from "work/proposals/negotiation/proposal-reviewer-approval-state";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import ConflictsTab from "work/proposals/_views/drafts/tabs/draft-proposal-conflicts-tab";
import DetailsTab from "work/proposals/_views/drafts/tabs/draft-proposal-details-tab";
import ClientTeamTab from "work/proposals/_views/drafts/tabs/draft-proposal-client-team-tab";
import DiscountTab from "work/proposals/_views/drafts/tabs/draft-proposal-discount-tab";
import FeeScheduleTab from "work/proposals/_views/drafts/tabs/draft-proposal-fee-schedule-tab";
import PoliciesTab from "work/proposals/_views/drafts/tabs/draft-proposal-policies-tab";
import TeamTab from "work/proposals/_views/drafts/tabs/draft-proposal-team-tab";
import {
  clearUnsavedCommentsByProposalId,
  markProposalCommentsAsRead,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentsDrawer from "work/proposals/comments/_view/comments-drawer";
import { useCommentPaneProposal } from "work/proposals/comments/comment-pane-proposal";
import {
  ProposalFieldHistoryRequestContext,
  ProposalFieldTraversalSource,
} from "work/proposals/negotiation/proposal-field-history-request-context";
import { ProposalFieldNavigationContext } from "work/proposals/negotiation/proposal-field-navigation-context";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import {
  getCommentThreadsByProposal,
  populateCommentThreadsByProposal,
} from "work/proposals/comments/threads/_store/comment-thread-redux-slice";
import IssueTraverser from "work/proposals/issues/_views/proposal-issue-traverser";
import ProposalIssuesBadge from "work/proposals/issues/_views/proposal-issues-badge";
import { proposalFieldUnread, proposalUnreadChangeFields } from "work/proposals/negotiation/proposal-field-unread";
import {
  proposalTimelineItemPathForField,
  proposalTimelinePathForField,
} from "work/proposals/negotiation/proposal-field-timeline-path";
import { proposalTraversalSurfacesCommentsPane } from "work/proposals/negotiation/proposal-traversal-comments-pane";
import { proposalFieldToMarkSeenOnTraversal } from "work/proposals/negotiation/proposal-field-traversal-seen";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import Proposal from "work/proposals/proposal";
import ProposalField, {
  ProposalFieldCategory,
  ProposalFieldCategoryType,
  ProposalFieldName,
} from "work/proposals/proposal-fields/proposal-field";
import ReviewerApprovalBadge from "work/proposals/reviewers/_views/reviewer-approval-badge";
import ReviewerSelector from "work/proposals/reviewers/_views/reviewer-selector";
import { useRequestProposalReReview } from "work/proposals/reviewers/_hooks/use-request-proposal-re-review";
import { getReviewerActionState } from "work/proposals/reviewers/_views/reviewer-selection";
import ProposalReviewer from "work/proposals/reviewers/proposal-reviewer";
import FeeSchedule from "work/work-agreements/fee-schedules/fee-schedule";
import SelectedVendorTeamLeader from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team-leader/selected-vendor-team-leader";
import SelectedVendorTeam from "work/work-agreements/teams/vendor-team/selection/selected-vendor-team/selected-vendor-team";
import WorkAgreement from "work/work-agreements/work-agreement";
// #endregion

// #region Styled Components
const ProposalActionSpan = styled("span")(({ theme }) => ({
  maxWidth: "22rem",
  minWidth: theme.spacing(24),
  width: "100%",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
    minWidth: 0,
    width: "100%",
  },
}));
const ReviewerActionMessage = styled("aside")(({ theme }) => ({
  alignSelf: "stretch",
  backgroundColor: alpha(theme.palette.info.main, 0.08),
  border: `1px solid ${alpha(theme.palette.info.main, 0.28)}`,
  borderRadius: theme.spacing(0.75),
  display: "grid",
  gap: theme.spacing(0.25),
  maxWidth: "22rem",
  minWidth: theme.spacing(24),
  padding: theme.spacing(0.75, 1),
  width: "100%",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
    minWidth: 0,
  },
}));
const ReviewerActionMessageBody = styled(Typography)(() => ({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const ReviewerActionMessageTooltipTitle = styled("span")(() => ({
  whiteSpace: "pre-wrap",
}));
const PageTitleLink = styled(Link)(() => ({
  color: "inherit",
  textDecoration: "none",
})) as typeof Link;
const NoBuilderLoader = styled(Loader)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  height: "100%",
  padding: theme.spacing(2),
  width: "100%",
}));
// #endregion

const EMPTY_REVIEWERS: ProposalReviewer[] = [];
const EMPTY_COMMENT_THREADS: CommentThread[] = [];

// The traverser walks the fields a draft is not finished with, which on a draft means its issues: the
// values missing or invalid for submission, found by ProposalIssues. "Change" is the active
// proposal's word — it describes a counterparty's edit in a live negotiation, and a draft nobody has
// submitted yet has none, so on this page it named something that was never on screen.
const ISSUE_TRAVERSER_PREV_LABEL = "Prev Issue";
const ISSUE_TRAVERSER_NEXT_LABEL = "Next Issue";

type DraftProposalAction = "edit" | "review" | "create" | "view";
const DraftProposalActions = new Set<DraftProposalAction>(["edit", "review", "create", "view"]);

export default function DraftProposalPage() {
  // #region Hooks
  const location = useLocation();
  const session = useSession();
  const navigate = useNavigate();
  const { action: actionParam, id: idParam, pageTab: pageTabParam } = useParams();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();
  const [searchParams] = useSearchParams();
  // What narrows the controls is the pane, not the window: the navigation drawer and this page's own
  // right-hand drawer both take width a media query cannot see (#4964).
  const { paneRef, paneWidth, isCompact: paneIsNarrow } = useWorkPaneSize();
  const paneTheme = useWorkPaneTheme(paneWidth);
  const canSeatSidePanel = useProposalPageCanSeatSidePanel();

  const pageAction: DraftProposalAction = (actionParam as DraftProposalAction) ?? "create";

  // Unsupported action
  if (!DraftProposalActions.has(pageAction)) {
    throw new Response("Not Found", { status: 404 });
  }
  // Proposal id is required for edit/review
  if (pageAction !== "create" && !idParam) {
    throw new Response("Not Found", { status: 404 });
  }

  // #region State
  const [panelState, dispatchPanelState] = useProposalPagePanels<
    FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
  >();
  const {
    selectedReviewField,
    commentsTitle: commentsPanelTitle,
    commentsOpen: commentsPanelIsOpen,
    addFeeOpen: isAddFeePanelOpen,
    existingFeeOpen: isExistingFeePanelOpen,
    teamSelectionOpen: isTeamSelectionPanelOpen,
    currentCategory,
    reviewerAnchor: reviewerSelectorAnchor,
    isManagingReviewers,
  } = panelState;

  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  // The submission decision — what to send, to whom, and whether to publish comments — is asked once,
  // against the envelope the pre-submit save just acknowledged, so the dialog is opened mid-submit and
  // awaited through this resolver.
  const { resolveSubmission, submitProposalDialog } = useSubmitProposalDialog();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isSelectingClient, setIsSelectingClient] = useState<boolean>(false);
  const [createdProposalIdAwaitingHydration, setCreatedProposalIdAwaitingHydration] = useState<string>();
  const [newFeeCategoryFormKey, setNewFeeCategoryFormKey] = useState(NEW_FEE_CATEGORY_DRAWER_KEY);
  // #endregion

  // #region Globals
  const proposalId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const preSelectedLeaderInfo = SelectedVendorTeamLeader.fromURLSearchParams(searchParams);
  const preSelectedTeamInfo = SelectedVendorTeam.fromURLSearchParams(searchParams);
  const preSelectedMarketplaceTeam = useSelector(getTeamById(preSelectedTeamInfo?.targetTeamId));
  const preSelectedMarketplaceTeamQuery = useGetTeamByIdQuery(
    preSelectedTeamInfo?.targetTeamId ? { teamId: preSelectedTeamInfo.targetTeamId.value } : skipToken,
  );
  const existingWorkAgreement: WorkAgreement = location.state?.workAgreement;
  const isReadOnlyView = pageAction === "view" || !pageAction;
  const activeTab = pageTabParam as PageTab;
  const isTeamSelectionDrawerOpen = activeTab === PageTab.VendorTeam && isTeamSelectionPanelOpen;

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  // #region Proposal Queries
  const collaboration = usePersistedProposalSaveOrchestration(proposalId?.value);
  // Deleted, or this viewer's access revoked, while they had it open — pushed over the websocket.
  const proposalWasDeleted = useSelector(getProposalIsNoLongerAvailable(proposalId));
  const proposalJson = collaboration.mode === "canonical" ? collaboration.envelope?.projection : undefined;
  const effectiveProposalJson =
    collaboration.mode === "canonical" ? collaboration.effectiveProposalJSON : proposalJson;
  const proposal = useMemo(() => {
    if (!proposalJson || isProposalLoadIssue(proposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: proposalJson,
      currentUser: session.user,
    });
  }, [proposalJson, session.user]);
  const effectiveProposal = useMemo(() => {
    if (!effectiveProposalJson || isProposalLoadIssue(effectiveProposalJson) || !session.user) return null;
    return Proposal.fromJSON({
      json: effectiveProposalJson,
      currentUser: session.user,
    });
  }, [effectiveProposalJson, session.user]);

  // #region Proposal Builder Queries
  const originalBuilderJSON = useSelector(getOriginalProposalBuilderByProposalId(proposalId ?? Guid.EMPTY));
  const legacyOriginalBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: originalBuilderJSON, session });
  }, [originalBuilderJSON, session]);

  const updatedBuilderJSON = useSelector(getUpdatedProposalBuilderByProposalId(proposalId ?? Guid.EMPTY));
  const legacyUpdatedBuilder = useMemo(() => {
    return ProposalBuilder.fromJSON({ json: updatedBuilderJSON, session });
  }, [updatedBuilderJSON, session]);
  const canonicalOriginalBuilder = useMemo(
    () =>
      collaboration.mode === "canonical" && proposal
        ? new ProposalBuilder({ session, startingSpec: proposal.spec })
        : null,
    [collaboration.mode, proposal, session],
  );
  const canonicalUpdatedBuilder = useMemo(
    () =>
      collaboration.mode === "canonical" && effectiveProposal
        ? new ProposalBuilder({ session, startingSpec: effectiveProposal.spec })
        : null,
    [collaboration.mode, effectiveProposal, session],
  );
  const canUseLegacyEditor = collaboration.mode === "disabled";
  const originalBuilder =
    collaboration.mode === "canonical"
      ? canonicalOriginalBuilder
      : canUseLegacyEditor
        ? legacyOriginalBuilder
        : null;
  const updatedBuilder =
    collaboration.mode === "canonical"
      ? canonicalUpdatedBuilder
      : canUseLegacyEditor
        ? legacyUpdatedBuilder
        : null;

  const updatedBuilderIssues = useMemo(() => {
    return updatedBuilder ? ProposalIssues.fromBuilder(updatedBuilder) : new ProposalIssues();
  }, [updatedBuilder]);
  const updatedBuilderIssuesRef = useRef<ProposalIssues>(updatedBuilderIssues);
  useEffect(() => {
    updatedBuilderIssuesRef.current = updatedBuilderIssues;
  }, [updatedBuilderIssues]);

  // #region Comments Queries
  // Comments are Messaging-native: a comment is a Message in a topic-filtered Forum keyed by the
  // durable negotiationId. We feed the comment layer a proposal whose id is that negotiationId so the
  // forum/thread/store keys align on it. Reading this store (also fed by the `messages` Ably
  // middleware) is what restores comment realtime. Drafts stay author-only and client-side pending
  // until Save publishes them to the Forum subscribers.
  const negotiationGuid = useMemo(() => {
    const id = collaboration.mode === "canonical" ? collaboration.envelope?.negotiationId : undefined;
    if (!id) return undefined;
    try {
      return new Guid(id);
    } catch {
      return undefined;
    }
  }, [collaboration.mode, collaboration.envelope?.negotiationId]);
  // Held through an envelope reload rather than re-derived on every render: the pane is rendered only
  // while this is present, and the composer inside it loses what is being typed if it is unmounted and
  // remounted underneath. See `useCommentPaneProposal`.
  const commentProposal = useCommentPaneProposal({
    routeProposalId: proposalId,
    proposal,
    negotiationId: collaboration.mode === "canonical" ? collaboration.envelope?.negotiationId : undefined,
    envelopeIsLoading: collaboration.isLoading,
  });
  // Whether there is a Comments/Changes pane to show at all. A draft that has never been persisted has
  // no negotiation behind it, so the pane renders nothing — and anything that "opens" it then reserves
  // the pane's width beside a pane that was never drawn, which is the blank column on the right of the
  // page. One flag decides all three: whether the drawer renders, whether its width is reserved, and
  // whether a caller may open it.
  const commentsPaneIsAvailable = Boolean(commentProposal?.createdDate);
  // The field whose change history the change traverser asked to open, published to the tabs by
  // context so the traverser can reach a control that lives inside whichever tab renders the field.
  const [fieldHistoryToOpen, setFieldHistoryToOpen] = useState<ProposalField | undefined>(undefined);
  const fieldHistoryRequest = {
    field: fieldHistoryToOpen,
    onHonored: () => setFieldHistoryToOpen(undefined),
  };
  const loadedCommentThreads = useSelector(getCommentThreadsByProposal(commentProposal));
  const commentThreads = loadedCommentThreads ?? EMPTY_COMMENT_THREADS;
  // The negotiation has one conversation, so this is its whole unread count.
  const unreadCommentCount =
    commentThreads?.reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0) ?? 0;
  // What each tab is holding that the counterparty cannot see yet: this actor's own staged comments
  // and their own queued changes. Distinct from the unread/unviewed badges beside them, which count
  // what somebody else did.
  const tabPendingCounts = useProposalTabPendingCounts(collaboration.pendingChanges.operations);
  // Resolves people and collection items for every change description this page renders — the save
  // confirmation, the submit dialog, and the field history all read from the same context.
  const changeDescriptionContext = useProposalChangeDescriptionContext(
    effectiveProposalJson ?? proposalJson,
    collaboration.envelope?.projection,
    collaboration.envelope?.changeLines,
  );
  const isLoading = collaboration.isLoading;

  const [createProposalQuery] = useCreateProposalMutation();
  const [saveProposalQuery, saveProposalResult] = useSaveProposalMutation();
  const [loadProposalCollaboration] = useLazyGetProposalCollaborationQuery();
  const [submitProposalCollaborationDraft] = useSubmitProposalCollaborationDraftMutation();
  const [markProposalChangesSeen] = useMarkProposalChangesSeenMutation();
  const [completeProposalReview, completeProposalReviewResult] = useCompleteProposalReviewMutation();
  const [deleteProposalQuery] = useDeleteProposalMutation();
  const [, giveReviewerApprovalQueryResult] = useGiveReviewerApprovalMutation();

  const isReviewing = useMemo(() => {
    return (
      [...(proposal?.clientReviewers ?? []), ...(proposal?.vendorReviewers ?? [])].some((reviewer) =>
        reviewer.userId.isEqualTo(session.user?.id),
      ) ?? false
    );
  }, [proposal, session.user]);
  const isViewingAsVendor = session.context?.viewingAsVendor;
  const vendorReviewers = updatedBuilder?.currentSpec.vendorReviewers;
  const clientReviewers = updatedBuilder?.currentSpec.clientReviewers;
  const reviewers = (isViewingAsVendor ? vendorReviewers : clientReviewers) ?? EMPTY_REVIEWERS;
  const reviewerBaseline =
    (isViewingAsVendor ? proposal?.vendorReviewers : proposal?.clientReviewers) ?? EMPTY_REVIEWERS;
  const updatedBuilderWithSavedReviewers = useMemo(() => {
    if (!proposal || !updatedBuilder) return updatedBuilder;
    return updatedBuilder.setClientReviewers(proposal.clientReviewers).setVendorReviewers(proposal.vendorReviewers);
  }, [proposal, updatedBuilder]);
  const builderDiffersFromOriginal = useMemo(
    () => Boolean(updatedBuilder?.isDifferentFrom(originalBuilder)),
    [originalBuilder, updatedBuilder],
  );
  const hasPendingNonReviewerBuilderChanges = useMemo(
    () => Boolean(proposal?.isModifiedByBuilder(updatedBuilderWithSavedReviewers)),
    [proposal, updatedBuilderWithSavedReviewers],
  );
  const hasPendingReviewerChanges =
    collaboration.mode === "canonical"
      ? collaboration.hasPendingChanges
      : Boolean(proposal?.isModifiedByBuilder(updatedBuilder));
  const reviewCompletionSharePlan = buildProposalSaveSharePlan({
    operations: collaboration.pendingChanges.operations,
    reviewerChanges: collaboration.pendingChanges.reviewerChanges,
    audienceLabel: proposalSaveAudienceLabel({
      reviewerCount: reviewers.length,
      isReviewing,
    }),
    context: changeDescriptionContext,
  });
  const reviewerHasCurrentApproval = (reviewer: ProposalReviewer) =>
    collaboration.mode === "canonical" && collaboration.envelope
      ? isReviewerApprovalCurrent(collaboration.envelope, {
          userId: reviewer.userId.value,
          legalEntityId: reviewer.entityId.value,
        })
      : Boolean(reviewer.dateApproved);
  const allReviewers: ProposalReviewer[] = [
    ...(proposal?.clientReviewers ?? []),
    ...(proposal?.vendorReviewers ?? []),
  ];
  const hasUserApprovedReview = allReviewers.some(
    (reviewer) => reviewer.userId.isEqualTo(session.user?.id) && reviewerHasCurrentApproval(reviewer),
  );
  const reviewCompletionLocksEditing = collaboration.mode !== "canonical" && hasUserApprovedReview;
  const canEditProposal =
    collaboration.mode === "canonical"
      ? Boolean(collaboration.capabilities?.canEditDraft)
      : !proposal || Boolean(session.user?.canEdit(proposal));
  // Editing the proposal and configuring its initial setup are two permissions, not one. A reviewer
  // granted edit rights may change the Vendor Team's ROSTER; its IDENTITY — the leader and the
  // marketplace team — stays with the creator until first submission. Offering the leader controls
  // on `canEditDraft` alone let a reviewer make a change the save path then dropped in silence, so
  // the team came back with new members under the old leader (#4854).
  const canChangeVendorTeamIdentity = !isBarredFromInitialProposalSetup(collaboration.capabilities);
  const reReview = useRequestProposalReReview({
    envelope: collaboration.mode === "canonical" ? collaboration.envelope : undefined,
    viewerScope: collaboration.viewerScope,
    canManageReviewers:
      collaboration.mode === "canonical" ? Boolean(collaboration.capabilities?.canManageReviewers) : false,
  });


  const issues = useMemo(() => ProposalIssues.fromBuilder(updatedBuilder ?? undefined), [updatedBuilder]);
  const issueFields = useMemo(() => {
    return uniqBy(issues?.entries.map((issue) => issue.field) ?? [], (field) => field.toJSON());
  }, [issues]);

  const proposalRef = useRef(proposal);
  // The proposal this editor has already created, held apart from `proposalRef` because the sync
  // effect below rewrites that one on EVERY render (its `collaboration` dependency is a fresh object
  // each time) — so the "I already created it" memory it used to carry survived only until the next
  // paint, and any save entered in the window before the saved route hydrates took the create branch
  // a second time. This ref is written exactly once, where the create resolves, and never cleared by
  // rendering.
  const createdProposalRef = useRef<Proposal | undefined>(undefined);
  // One save at a time. Checked and set synchronously — before `setIsSaving`, before any await — so
  // re-entrancy is impossible regardless of when React commits. The header buttons are gated by
  // `disableSave`, but the discard-changes prompt's "Save & Close" is not, and that is the path a
  // reviewer edit ends on.
  const saveInFlightRef = useRef<Promise<string | null | undefined> | null>(null);
  // The idempotency key for creating this draft: minted once for the editing session and resent
  // unchanged on every attempt, so a create that reaches the server twice resolves to one proposal.
  // A fresh uuid per attempt would guard nothing.
  const draftCreationCommandIdRef = useRef<string>(crypto.randomUUID());
  const collaborationRef = useRef(collaboration);
  const commentProposalRef = useRef(commentProposal);
  const updatedBuilderRef = useRef(updatedBuilder);
  // `handleSaveChanges` is memoized on `[saveProposalResult.error]` alone, so its closure is effectively
  // frozen at first render — when nobody has been fetched and no team has been chosen yet. Reading the
  // context through a ref is what lets the save confirmation describe the page as it is NOW; without it
  // every change that needs a person's name ("Set Team Leader", "Added Team Member") rendered nameless.
  const changeDescriptionContextRef = useRef<ProposalChangeDescriptionContext>({});
  const builderStorageIdRef = useRef(proposalId ?? Guid.EMPTY);
  const isDeletingProposalRef = useRef(false);
  const isSubmittingProposalRef = useRef(false);
  const appDirectedNavigationPathRef = useRef<string | null>(null);
  const networkRequestIsInFlight = isLoading || isSaving || isSubmitting || completeProposalReviewResult.isLoading;
  const createdProposalRouteIsHydrated = Boolean(
    createdProposalIdAwaitingHydration &&
      proposalId?.value === createdProposalIdAwaitingHydration &&
      proposal?.id?.value === createdProposalIdAwaitingHydration &&
      updatedBuilder,
  );
  const createdProposalRouteIsTransitioning = Boolean(
    createdProposalIdAwaitingHydration &&
      (!proposalId || proposalId.value === createdProposalIdAwaitingHydration) &&
      !createdProposalRouteIsHydrated,
  );
  const pageControlsArePaused = networkRequestIsInFlight || createdProposalRouteIsTransitioning;

  // Whether the proposal's source can still change its answer. An error is a settled answer, not a
  // pending one — but only when it is terminal: a deleted draft answers 404 and the viewer must be sent
  // back, while a transient failure must leave them where they are rather than ejecting them mid-edit.
  //
  // Derived out here rather than inside the effect below so the effect depends on this one boolean
  // instead of three fields of a re-created object — including `collaboration.error`, which the effect
  // read while the dependency list did not name it.
  const proposalSourceIsResolving =
    collaboration.isLoading ||
    (collaboration.mode === "error" && !isProposalNoLongerAvailableError(collaboration.error));

  // #region Lifecycle
  useEffect(() => {
    let submittedProposalAction: "review" | "revise" | "view" | undefined;
    if (pageAction === "review") submittedProposalAction = "review";
    else if (pageAction === "edit") submittedProposalAction = "revise";
    // A citation of a draft is minted from persisted status, so it can name a proposal that has
    // been submitted since. Read-only reroutes the same way the editing actions do.
    else if (pageAction === "view") submittedProposalAction = "view";
    // If opening a submitted proposal through an old draft URL, reroute to the active flow.
    if (!isDeletingProposalRef.current && submittedProposalAction && proposal?.isSubmitted) {
      const activeProposalPath = `/proposals/active/${submittedProposalAction}/${proposal.id?.value}/details`;
      appDirectedNavigationPathRef.current = activeProposalPath;
      navigate(activeProposalPath, { replace: true });
      return;
    }

    // Deleted (or access revoked) under the viewer while they had it open. This arrives over the
    // websocket, so nothing about the page's own load state changes — the canonical envelope still holds
    // its last good projection and the editor would happily keep going. Eject on the same terms as
    // opening a deleted proposal, before the not-found reasoning below, which cannot see this.
    if (proposalWasDeleted && !isDeletingProposalRef.current) {
      enqueueSnackbar("This proposal is no longer available.", { variant: "warning" });
      appDirectedNavigationPathRef.current = "/proposals/drafts";
      navigate("/proposals/drafts", { replace: true });
      return;
    }

    // Proposal invalid/not found/no longer reviewer.
    // `proposal` is also null while a proposal is still being resolved, so the absence of a
    // proposal only means "not found" once the canonical envelope has settled.
    const shouldShowNotFound =
      Boolean(proposalId) &&
      !proposalId?.isEmpty &&
      proposal === null &&
      pageAction !== "create" &&
      !proposalSourceIsResolving &&
      !isDeletingProposalRef.current &&
      !isSubmittingProposalRef.current;

    if (shouldShowNotFound) {
      if (pageAction === "review" && idParam) {
        enqueueSnackbar("You are no longer a reviewer on this proposal.", { variant: "warning" });
      } else if (idParam) {
        enqueueSnackbar("Proposal not found.", { variant: "warning" });
      }
      appDirectedNavigationPathRef.current = "/proposals/drafts";
      navigate("/proposals/drafts", { replace: true });
    }
  }, [
    pageAction,
    activeTab,
    navigate,
    proposal,
    proposalId,
    proposalSourceIsResolving,
    idParam,
    proposalWasDeleted,
  ]);

  // Keep refs in sync
  useEffect(() => {
    proposalRef.current = proposal;
    collaborationRef.current = collaboration;
    commentProposalRef.current = commentProposal;
    updatedBuilderRef.current = updatedBuilder;
    changeDescriptionContextRef.current = changeDescriptionContext;
  }, [
    proposal,
    collaboration,
    commentProposal,
    updatedBuilder,
    changeDescriptionContext,
  ]);

  // #region Populate Comment Threads
  // Loads the negotiation's comment Forums into the Forum/Message comment store the drawer and per-field
  // badges read from (and the Ably `messages` middleware updates).
  useEffect(() => {
    if (!commentProposal?.id || isLoading || loadedCommentThreads) return;
    void dispatch(populateCommentThreadsByProposal({ proposal: commentProposal, session }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentProposal?.id?.value, isLoading, loadedCommentThreads, session]);

  useEffect(() => {
    if (proposalId) {
      builderStorageIdRef.current = proposalId;
    } else if (!createdProposalIdAwaitingHydration) {
      builderStorageIdRef.current = Guid.EMPTY;
    }
  }, [createdProposalIdAwaitingHydration, proposalId]);


  // #region Functions
  /**
   * Reading a field's thread is what marks its comments read (ReadReceipt → createBulkMessages).
   *
   * Extracted because the traverser surfaces the pane WITHOUT going through
   * {@link handleOpenCommentsPanel} — it dispatches the panel state itself — and so left the comments it
   * had just surfaced unread.
   */
  const markProposalCommentsRead = useCallback(() => {
    const commentProposalId = commentProposalRef.current?.id;
    if (!commentProposalId) return;
    void dispatch(
      markProposalCommentsAsRead({
        session,
        proposalId: commentProposalId,
        commentThreads,
      }),
    );
  }, [dispatch, session, commentThreads]);

  const handleOpenCommentsPanel = useCallback(
    (field?: ProposalField, title?: string) => {
      dispatchPanelState({ type: "openComments", field, title, preserveTitleWhenUndefined: true });
      markProposalCommentsRead();
    },
    [dispatchPanelState, markProposalCommentsRead],
  );

  const handleOpenGeneralCommentsPanel = useCallback(() => {
    handleOpenCommentsPanel(ProposalField.General, "");
  }, [handleOpenCommentsPanel]);

  // Builder initialization
  useEffect(() => {
    if (proposalId && !canUseLegacyEditor) return;
    if (proposalId && !proposal) return; // still loading proposal
    if (updatedBuilderJSON || updatedBuilder) return;
    if (preSelectedTeamInfo?.targetTeamId && !preSelectedMarketplaceTeam && !preSelectedMarketplaceTeamQuery.isError) {
      return;
    }

    const initializedBuilder =
      originalBuilder ??
      new ProposalBuilder({
        session,
        startingSpec: proposal?.spec,
        existingWorkAgreement,
        preSelectedClientLeader:
          session.context?.viewingAsVendor && preSelectedLeaderInfo?.hasSigningAuthority
            ? preSelectedLeaderInfo?.toEntityMember()
            : undefined,
        preSelectedVendorLeader:
          !session.context?.viewingAsVendor && preSelectedLeaderInfo?.hasSigningAuthority
            ? preSelectedLeaderInfo?.toEntityMember()
            : undefined,
        preSelectedTeam: preSelectedMarketplaceTeam?.toWorkTeam(),
      });
    dispatch(
      replaceOriginalProposalBuilder({
        proposalId: proposalId ?? Guid.EMPTY,
        builder: initializedBuilder,
      }),
    );
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: proposalId ?? Guid.EMPTY,
        builder: initializedBuilder,
      }),
    );
  }, [
    proposal,
    proposalId,
    updatedBuilder,
    updatedBuilderJSON,
    originalBuilder,
    preSelectedLeaderInfo,
    preSelectedTeamInfo?.targetTeamId,
    preSelectedMarketplaceTeam,
    preSelectedMarketplaceTeamQuery.isError,
    session,
    session.context?.viewingAsVendor,
    existingWorkAgreement,
    dispatch,
    canUseLegacyEditor,
  ]);

  // Account view switching
  useEffect(() => {
    if (collaboration.mode === "canonical" && collaboration.envelope) {
      const accountType =
        collaboration.envelope.viewingParty === "Vendor" ? AccountType.Vendor : AccountType.Client;
      const alreadyViewingParty =
        session.context?.viewingAsVendor === (accountType === AccountType.Vendor);

      if (!alreadyViewingParty) void session.setAccountViewType(accountType);
      return;
    }

    // Don't switch account type if we're reviewing
    if (!proposal) return;
    if (
      (isReviewing &&
        !session.context?.viewingAsVendor &&
        proposal.clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(session.user?.id))) ||
      (session.context?.viewingAsVendor &&
        proposal.vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(session.user?.id)))
    )
      return;

    // If viewing as vendor and are the client team leader, switch to client account
    if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(proposal.clientLeader) &&
      session.context?.viewingAsVendor &&
      proposal.clientLeader?.userId.isEqualTo(session.user?.id)
    ) {
      void session.setAccountViewType(AccountType.Client);
    }

    // If viewing as client and are the vendor team leader, switch to vendor account
    if (
      MaskedTeam.isPresentNotMasked(proposal.vendorTeam) &&
      !session.context?.viewingAsVendor &&
      (proposal.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        proposal.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      void session.setAccountViewType(AccountType.Vendor);
    }
  }, [collaboration.envelope, collaboration.mode, isReviewing, proposal, session]);

  const activeReviewField = useMemo(() => {
    if (!updatedBuilder) return undefined;

    const proposalFieldCategories = Object.values(ProposalFieldCategory);
    const proposalFieldNames = Object.values(ProposalFieldName);
    const currentCategory = proposalFieldCategoryFromPageTab(activeTab);

    if (selectedReviewField?.category === currentCategory) return selectedReviewField;

    const tabIssues: ProposalField[] = issueFields.filter((field) => field.category === currentCategory) ?? [];

    return chain(tabIssues)
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ""}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name),
      ])
      .head()
      .value();
  }, [activeTab, issueFields, selectedReviewField, updatedBuilder]);

  const submitReviewTooltip = (() => {
    if (hasUserApprovedReview && hasPendingReviewerChanges) return "Save updates.";
    if (hasUserApprovedReview) return "Review complete. You can still comment.";
    if (!hasPendingReviewerChanges) return "Finish reviewing proposal.";
    return "Save updates and finish reviewing proposal.";
  })();

  function closeTabScopedPanels() {
    dispatchPanelState({ type: "closeTabScopedPanels" });
  }

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    if (pageControlsArePaused) return;
    closeTabScopedPanels();

    if (!proposalId) {
      navigate(`/proposals/drafts/create/${newTab}`);
      return;
    }
    navigate(`/proposals/drafts/${actionParam}/${proposalId.value}/${newTab}`);
  }

  const handleTeamSelectionDrawerOpenChange = useCallback(
    (open: boolean) => {
      dispatchPanelState({ type: "setTeamSelectionOpen", open });
    },
    [dispatchPanelState],
  );

  function proposalFieldHasUnreadChange(field: ProposalField): boolean {
    const envelope = collaboration.mode === "canonical" ? collaboration.envelope : undefined;
    return proposalFieldUnread(envelope, field, session.user?.id?.value).unreadCount > 0;
  }

  function markTraversedFieldChangesSeen(destinationField: ProposalField) {
    const envelope = collaboration.mode === "canonical" ? collaboration.envelope : undefined;
    const field = proposalFieldToMarkSeenOnTraversal({
      sidePaneIsOpen: commentsPanelIsOpen,
      currentField: activeReviewField,
      destinationField,
    });
    // A fee-schedule category acknowledges itself and not its siblings (#5118).
    const path = field && (proposalTimelineItemPathForField(field) ?? proposalTimelinePathForField(field));
    if (!envelope || !path) return;
    const { unreadCount, latestUnseenSequence } = proposalFieldUnread(envelope, field, session.user?.id?.value);
    if (unreadCount > 0) {
      void markProposalChangesSeen({
        negotiationId: envelope.negotiationId,
        path,
        throughStreamVersion: latestUnseenSequence,
      });
    }
  }

  const handleTraverseToNewField = useCallback(
    (field?: ProposalField, source: ProposalFieldTraversalSource = ProposalFieldTraversalSource.Field) => {
      if (pageControlsArePaused) return;
      if (!field) {
        dispatchPanelState({ type: "setSelectedReviewField", field, closePanels: true });
        return;
      }

      // A stop on an unread change is a stop about that change, so it opens that field's own change
      // history rather than the proposal's one conversation, which is what the pane shows and which is
      // not what the reader was sent here to read. Asked BEFORE acknowledging, since acknowledging is
      // what makes the change no longer unread.
      //
      // Only "Prev / Next Change" opens it. This handler is also every field's own traverse-in —
      // focusing a field, or clicking its Edit button, reports itself here — and a reader who reached
      // for Edit asked to type, not to read history.
      const stoppedOnUnreadChange =
        source === ProposalFieldTraversalSource.Traverser && proposalFieldHasUnreadChange(field);
      // A Prev/Next press opens the history or nothing at all — never the pane. An issue stop answers
      // itself where it lands: setting the review field below scrolls the highlighted row into view.
      const surfaceCommentsPane =
        commentsPaneIsAvailable && proposalTraversalSurfacesCommentsPane({ source, field });
      // Stopping on a field is also reading it: surface what the stop is about and acknowledge what it
      // surfaced, so pressing Next repeatedly walks the unread changes down to none and the traverser
      // disables itself. A draft with no pane to surface just moves to the field; asking for a pane
      // that cannot render only opens a gap where it would have been.
      markTraversedFieldChangesSeen(field);
      dispatchPanelState({
        type: "setSelectedReviewField",
        field,
        closePanels: true,
        openComments: surfaceCommentsPane,
      });
      if (stoppedOnUnreadChange) {
        setFieldHistoryToOpen(field);
      } else {
        // Moving on answers any standing request: leaving the previous field's history asked-for would
        // hold that panel open over a field the reader has already left.
        setFieldHistoryToOpen(undefined);
      }
      // The pane also shows the negotiation's conversation, so surfacing it acknowledges those comments —
      // and only surfacing it does. A stop that leaves the pane shut has shown the reader nothing to
      // acknowledge, so marking the conversation read there would retire unread comments unseen.
      if (surfaceCommentsPane) markProposalCommentsRead();

      const targetTab = pageTabFromProposalFieldCategory(field.category);
      const currentTabCategory = proposalFieldCategoryFromPageTab(activeTab);

      if (field.category !== currentTabCategory) {
        const action = actionParam ?? "create";

        if (proposalId?.value) {
          navigate(`/proposals/drafts/${action}/${proposalId.value}/${targetTab}`, {
            replace: true,
          });
        } else {
          navigate(`/proposals/drafts/create/${targetTab}`, {
            replace: true,
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeTab,
      actionParam,
      commentsPaneIsAvailable,
      dispatchPanelState,
      markProposalCommentsRead,
      navigate,
      pageControlsArePaused,
      proposalId,
    ],
  );

  /** A deliberate "Prev / Next Change" press — the only traversal that opens the stop's own history. */
  const handleTraverseFromTraverser = useCallback(
    (field?: ProposalField) => handleTraverseToNewField(field, ProposalFieldTraversalSource.Traverser),
    [handleTraverseToNewField],
  );


  // The anchor is passed in rather than read off the event, because the control the selector hangs
  // from is not always this action's own button: once the secondary actions collapse it is the "More
  // Actions" button, and the menu item that was pressed is gone by the time the popover opens.
  function handleManageReviewersClicked(anchor: HTMLButtonElement) {
    dispatchPanelState({ type: "openReviewerSelector", anchor });
  }

  async function handleSubmitClicked() {
    if (!updatedBuilder) {
      throw new Error("Proposal cannot be submitted without a builder.");
    }

    // Reviewer changes are resolved in-field as targets, so an outstanding reviewer approval does not
    // gate submission: the dialog warns that a review is still pending (#5128) and submits anyway.
    //
    // The submission asks exactly one question, and it asks it against the SAVED state: the pending
    // edits are flushed first so the dialog can name the real change list (and let the leader withhold
    // individual changes) instead of confirming blind and asking a second time afterwards. A flush is
    // not a commitment — the work is saved either way the moment they press Save; only submitting
    // sends it to the other party.
    isSubmittingProposalRef.current = true;
    // Submission begins with an internal save so the dialog can describe the acknowledged changes.
    // Surface that whole operation as Submit work; otherwise the button remains idle during the
    // slowest part of the click and the page looks hung.
    setIsSubmitting(true);

    try {
      const submissionMode = collaborationRef.current.mode;
      const draftProposalIdValue = await handleSaveChanges({
        confirmSave: false,
      });
      if (!draftProposalIdValue) {
        throw new Error("Failed to save proposal before submission.");
      }

      let submittedProposalIdValue: string | undefined;
      const viewerScope = collaborationRef.current.viewerScope;
      if (!viewerScope) throw new Error("Proposal collaboration viewer is not ready for submission.");

      if (submissionMode === "canonical") {
        // `savePendingChanges` reads Redux at invocation time. Calling it after
        // the page save gives submission the acknowledged envelope cursors,
        // never the envelope captured by the render that started this click.
        let submissionEnvelope = await collaborationRef.current.savePendingChanges();
        if (!submissionEnvelope) {
          throw new Error("Canonical proposal save returned no envelope before submission.");
        }
        const submission = await resolveSubmission({
          envelope: submissionEnvelope,
          title: "Submit Proposal",
          confirmButtonText: "Submit",
          context: changeDescriptionContext,
        });
        if (submission === CANCELED_SUBMISSION) return;
        if (submission.revertSelection.length > 0) {
          const revertOperations = buildProposalSubmissionRevertOperations({
            envelope: submissionEnvelope,
            selections: submission.revertSelection,
          });
          if (revertOperations.length !== submission.revertSelection.length) {
            throw new Error("One or more proposal changes could not be reverted before submission.");
          }
          dispatch(
            proposalOperationsQueued({
              negotiationId: submissionEnvelope.negotiationId,
              operations: revertOperations,
            }),
          );
          const revertedEnvelope = await collaborationRef.current.savePendingChanges();
          if (!revertedEnvelope) {
            throw new Error("Reverted proposal changes could not be saved before submission.");
          }
          submissionEnvelope = revertedEnvelope;
        }
        const submittedEnvelope = await submitProposalCollaborationDraft({
          negotiationId: submissionEnvelope.negotiationId,
          viewerScope,
          command: buildSubmitProposalDraftCommand({
            envelope: submissionEnvelope,
          }),
        }).unwrap();
        dispatch(proposalEnvelopeReceived({ envelope: submittedEnvelope, viewerScope }));
        submittedProposalIdValue = submittedEnvelope.projection.id ?? undefined;
      } else if (submissionMode === "disabled") {
        // A brand-new proposal has no id (and therefore no negotiation hook)
        // until its initial create completes. Resolve that newly-created
        // stream, then submit through the same typed command as every other
        // canonical draft.
        const submissionEnvelope = await loadProposalCollaboration(
          { proposalId: draftProposalIdValue, viewerScope },
          false,
        ).unwrap();
        // A first submission has nothing published to withhold, so the dialog reads as a confirmation
        // with the proposal's own terms listed — but it is the same dialog, asked the same way.
        const submission = await resolveSubmission({
          envelope: submissionEnvelope,
          title: "Submit Proposal",
          confirmButtonText: "Submit",
          context: changeDescriptionContext,
        });
        if (submission === CANCELED_SUBMISSION) return;
        const submittedEnvelope = await submitProposalCollaborationDraft({
          negotiationId: submissionEnvelope.negotiationId,
          viewerScope,
          command: buildSubmitProposalDraftCommand({
            envelope: submissionEnvelope,
          }),
        }).unwrap();
        dispatch(proposalEnvelopeReceived({ envelope: submittedEnvelope, viewerScope }));
        submittedProposalIdValue = submittedEnvelope.projection.id ?? undefined;
      } else {
        throw new Error("Proposal collaboration is not ready for submission.");
      }

      if (!submittedProposalIdValue) {
        throw new Error("Submitted proposal did not return an id.");
      }

      const submittedProposalId = new Guid(submittedProposalIdValue);
      dispatch(clearLocalProposalById(submittedProposalId));
      dispatch(clearProposalBuilderByProposalId(submittedProposalId));
      if (proposalId && !proposalId.isEqualTo(submittedProposalId)) {
        dispatch(clearLocalProposalById(proposalId));
        dispatch(clearProposalBuilderByProposalId(proposalId));
      }
      if (!proposalId) dispatch(clearNewProposalBuilder());

      enqueueSnackbar("Proposal submitted successfully.", {
        variant: "success",
      });

      navigate(`/proposals/active/revise/${submittedProposalIdValue}/details`, { replace: true });
    } catch (error) {
      console.error(error);
      // A `409` is a genuine content conflict, not a transport failure: the
      // other party changed a field this draft also touched. Name the fields
      // instead of reporting a bare failure the user cannot act on.
      const conflictSummary = describeProposalConflicts(
        readProposalConflictResponse(error)?.conflicts ?? [],
      );
      // A `400` means the command itself was refused — most often a draft that fails validation, which
      // is persisted and so fails identically after a refresh. Reporting a bare failure there gave the
      // user a dead end: nothing to fix, and no reason to believe retrying would ever work.
      const commandError = conflictSummary ?? readProposalCommandErrorDetail(error);
      enqueueSnackbar(commandError ?? "Failed to submit proposal.", {
        variant: "error",
        ...(commandError ? { autoHideDuration: 12000 } : {}),
      });
    } finally {
      isSubmittingProposalRef.current = false;
      setIsSaving(false);
      setIsSubmitting(false);
    }
  }

  function shouldDisableTabs(): boolean {
    return pageControlsArePaused || isManagingReviewers || isSelectingClient;
  }

  async function handleDeleteClicked() {
    if (proposal?.isArchived) return;
    if (!proposal?.id) {
      console.warn("Proposal not found.");
      return;
    }

    try {
      const rejectResponse = await confirm({
        title: "Delete Draft Proposal?",
        message: "This cannot be undone.",
      });

      if (rejectResponse === ConfirmResponse.Cancel) return;

      setIsDeleting(true);
      isDeletingProposalRef.current = true;
      await deleteProposalQuery({ proposalId: proposal.id.value }).unwrap();
      handleProposalDraftCleanup(proposal.id);
      enqueueSnackbar("Proposal deleted.", { variant: "info" });
      navigate("/proposals/drafts", { replace: true });
    } catch (error) {
      isDeletingProposalRef.current = false;
      console.error(error);
      enqueueSnackbar("Failed to delete proposal.", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleProposalBuilderUpdated(updatedProposalBuilder: ProposalBuilder | undefined) {
    if (reviewCompletionLocksEditing) {
      return;
    }
    if (!updatedProposalBuilder) {
      console.warn("handleProposalBuilderUpdated called with empty proposal builder");
      return;
    }
    if (collaboration.mode === "canonical") {
      if (!effectiveProposal) throw new Error("Canonical proposal projection is unavailable.");
      updatedBuilderRef.current = updatedProposalBuilder;
      const updatedProposal = effectiveProposal.isSubmitted
        ? updatedProposalBuilder.updateSubmittedDraftReviewProposal(effectiveProposal)
        : updatedProposalBuilder.updateDraftProposal(effectiveProposal);
      collaboration.queueProposalUpdate(updatedProposal);
      return;
    }
    const builderToStore = getDraftBuilderForUpdate({
      originalBuilder,
      proposal,
      updatedBuilder: updatedProposalBuilder,
    });
    // Keep imperative actions (save, reviewer send, and cross-tab edits) on the
    // exact builder that was just produced. A debounced input flushes on blur,
    // before the following click; deferring this Redux update as a transition
    // allowed that click to retain and later save the previous render's builder.
    updatedBuilderRef.current = builderToStore;
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: builderStorageIdRef.current,
        builder: builderToStore,
      }),
    );
  }

  const handleProposalDraftCleanup = useCallback(
    (proposalId: Guid | undefined) => {
      if (proposalId && !proposalId.isEmpty) {
        dispatch(clearUnsavedCommentsByProposalId(proposalId));
        if (collaboration.mode === "canonical") {
          collaboration.discardPendingChanges();
        } else {
          dispatch(clearProposalBuilderByProposalId(proposalId));
        }
      } else {
        dispatch(clearNewProposalBuilder());
      }
    },
    [collaboration, dispatch],
  );

  const handleCloseCommentsPanel = () => {
    dispatchPanelState({ type: "closeComments" });
  };

  async function completeCurrentReview() {
    const currentCollaboration = collaborationRef.current;
    if (currentCollaboration.mode === "canonical") {
      // Always read through the hook after Save; it returns the latest Redux
      // envelope even when there is no remaining command to send.
      const freshEnvelope = await currentCollaboration.savePendingChanges();
      if (!freshEnvelope || !currentCollaboration.viewerScope) {
        throw new Error("Canonical proposal review is missing its authoritative envelope.");
      }
      await completeProposalReview({
        negotiationId: freshEnvelope.negotiationId,
        viewerScope: currentCollaboration.viewerScope,
        command: buildCompleteProposalReviewCommand({ envelope: freshEnvelope }),
      }).unwrap();
      return;
    }

    throw new Error("Proposal collaboration is not ready to complete a review.");
  }

  async function handleFinalizeReviewClicked() {
    if (!proposal?.id) throw new Error("No proposal to finalize review for.");

    try {
      // The review button routes pending local work through handleSubmitAllAndFinalizeReviewClicked, so
      // a plain "Complete Review" is a versioned endorsement of the freshest canonical envelope and
      // nothing else — `completeCurrentReview` reads that envelope through `savePendingChanges`, which
      // sends a command only when something is actually queued (ARCHITECTURE.md, "Complete review":
      // completion "runs after pending edits are saved", and a reviewer with no pending edits has none
      // to save). This used to pre-save unconditionally. A comment-only reviewer (#4535) has neither
      // `canEditDraft` nor `canManageReviewers`, so that save tripped its own permission gate, returned
      // no id, and turned a legitimate completion into "You do not have permission to save updates to
      // this proposal." followed by "Failed to finalize proposal review." — with no request ever sent.
      await completeCurrentReview();
      handleProposalDraftCleanup(proposal.id);
      enqueueSnackbar("Proposal review finalized.", { variant: "success" });
      navigate("/proposals/drafts", { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to finalize proposal review.", {
        variant: "error",
      });
    }
  }

  async function handleSubmitAllAndFinalizeReviewClicked() {
    if (!proposalRef.current?.id) throw new Error("No proposal to finalize review for.");

    try {
      // Only the reviewer's proposal-content work. "Save & Complete Review" used to also flush the
      // reviewer's comments, because a review whose comments stayed in the browser was delivered to
      // nobody; a comment reaches its audience when it is written now, so completing a review has
      // nothing of the conversation left to send.
      // `handleSaveChanges` refuses to persist for an actor who may not, so asking it to save
      // unsendable work would only fail the completion. A comment-only reviewer can still be holding
      // queued content — a durable local queue built while they had edit rights that were later
      // revoked — and that work is unsendable by definition: the server rejects a save carrying any
      // operation from an actor without `canEditDraft` (#4535). Skip the save and endorse the current
      // version; the local work stays queued rather than being destroyed.
      if (hasPendingReviewerChanges && collaboration.canPersistProposalUpdates(proposalRef.current)) {
        const savedProposalId = await handleSaveChanges({
          confirmSave: false,
        });

        if (!savedProposalId) {
          throw new Error("Failed to save proposal updates before finalizing review.");
        }
      }

      await completeCurrentReview();
      handleProposalDraftCleanup(proposalRef.current.id);
      enqueueSnackbar("Proposal review finalized.", { variant: "success" });
      navigate("/proposals/drafts", { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to finalize proposal review.", {
        variant: "error",
      });
    }
  }

  const persistProposalChanges = useCallback(
    async (args?: {
      proposal?: Proposal;
      proposalBuilder?: ProposalBuilder;
      confirmSave?: boolean;
      confirmationMode?: ConfirmationMode;
    }): Promise<string | null | undefined> => {
      const { proposal, proposalBuilder, confirmSave = true, confirmationMode = "review" } = args ?? {};

      // This handler keeps a stable identity and reads mutable state through refs. The
      // collaboration must come through its ref too: read from the closure it would be
      // whatever mode was current on first render — still "loading" — which sent every
      // save down the legacy path while edits queued canonically and were left behind.
      const collaboration = collaborationRef.current;

      // A proposal whose source failed to answer is a paused page, not an empty one. `proposal` is
      // null while the route still names a draft, so a save let through here would write a SECOND
      // draft beside the one that could not be loaded. Loading already pauses the page; a load error
      // has to as well.
      if (collaboration.mode === "error") {
        enqueueSnackbar("This proposal could not be loaded, so it cannot be saved right now.", {
          variant: "error",
        });
        return null;
      }

      let proposalToSave = proposal ?? proposalRef.current ?? createdProposalRef.current;
      const builderToSave = proposalBuilder ?? updatedBuilderRef.current;

      setIsSaving(true);

      try {
        if (!proposalToSave) {
          proposalToSave = builderToSave?.buildNewDraft();
        } else if (builderToSave) {
          proposalToSave = proposalToSave.isSubmitted
            ? builderToSave.updateSubmittedDraftReviewProposal(proposalToSave)
            : builderToSave.updateDraftProposal(proposalToSave);
        }

        if (!proposalToSave) {
          console.error("No proposal to save");
          enqueueSnackbar("There was a problem saving the proposal.", {
            variant: "error",
          });
          return;
        }

        const updatedReviewers = session.user?.isClient(proposalToSave)
          ? proposalToSave.clientReviewers
          : proposalToSave?.vendorReviewers;
        const baselineProposal = proposal ?? proposalRef.current ?? createdProposalRef.current;
        const baselineProposalToSave = baselineProposal ?? proposalToSave;
        const originalReviewers =
          (session.user?.isClient(proposalToSave)
            ? baselineProposalToSave.clientReviewers
            : baselineProposalToSave.vendorReviewers) ?? [];
        const builderWithSavedReviewers =
          baselineProposal && builderToSave
            ? builderToSave
                .setClientReviewers(baselineProposal.clientReviewers)
                .setVendorReviewers(baselineProposal.vendorReviewers)
            : undefined;
        const hasNonReviewerBuilderUpdates = Boolean(baselineProposal?.isModifiedByBuilder(builderWithSavedReviewers));
        const reviewerActionState = getReviewerActionState({
          originalReviewers,
          selectedReviewers: updatedReviewers,
          proposalIsModifiedByBuilder: hasNonReviewerBuilderUpdates,
          proposalHasUnsavedRedlineChanges: false,
        });
        const hasReviewerContext = updatedReviewers.length > 0 || reviewerActionState.hasReviewerChanges;

        if (confirmSave) {
          // Describe exactly what the command about to be sent carries — the queued operations and
          // reviewer changes — in the same words the field change history uses, and name who receives
          // it. A builder-derived log described a different set in a different vocabulary.
          const sharePlan = buildProposalSaveSharePlan({
            operations: collaboration.pendingChanges.operations,
            reviewerChanges: collaboration.pendingChanges.reviewerChanges,
            audienceLabel: proposalSaveAudienceLabel({
              reviewerCount: updatedReviewers.length,
              isReviewing,
            }),
            // Through the ref: this callback's closure predates the individuals cache and the team.
            context: changeDescriptionContextRef.current,
          });
          const confirmationCopy = getSaveConfirmationCopy({
            confirmationMode,
            hasPendingChanges: hasNonReviewerBuilderUpdates,
            hasReviewerChanges: reviewerActionState.hasReviewerChanges,
            hasReviewerContext,
            hasReviewerRecipients: updatedReviewers.length > 0,
            proposalIsSaved: Boolean(proposalToSave.id),
            reviewerConfirmMessage: reviewerActionState.confirmMessage,
            reviewerConfirmOkButtonText: reviewerActionState.confirmOkButtonText,
            reviewerConfirmSupplementalMessage: reviewerActionState.confirmSupplementalMessage,
            reviewerConfirmTitle: reviewerActionState.confirmTitle,
            audienceLabel: sharePlan.audienceLabel,
            // The dialog renders this list below the message, so the message stops summarising it.
            describedChangeCount: sharePlan.contentChangeCount,
            describedReviewerChangeCount: sharePlan.reviewerChangeCount,
          });

          const response = await confirm({
            title: confirmationCopy.title,
            // The change list renders rather than flattening into the message string, so it keeps its
            // bullets and shows each value transition as a redline instead of plain grey text. Suppressed
            // entirely on a draft's first-ever save (#4952): there is no prior version to diff against,
            // so the list would only repeat what the actor just typed.
            message: (
              <>
                {confirmationCopy.message}
                {confirmationCopy.showsChangeSummary && (
                  <ProposalChangeSummaryList
                    heading={confirmationCopy.changeLogHeading}
                    descriptors={sharePlan.descriptors}
                  />
                )}
              </>
            ),
            okButtonText: confirmationCopy.okButtonText,
            // Deliberately no comment option. A save used to offer "Save and publish pending internal
            // comments", because a comment sat in the browser until one did; comments are written as
            // they are made, so a save has nothing of the conversation to offer.
          });

          if (response === ConfirmResponse.Cancel) return null;
        }

        const canPersistProposalUpdates = collaboration.canPersistProposalUpdates(proposalToSave);

        // A comment-only reviewer used to be let through here so their staged comments could be
        // published by the save; they have none to publish, because their comments went out as they
        // wrote them. Save is once again exactly about proposal-content and reviewer changes.
        // A staged client team roster is written by this same press and is gated by its own
        // capability, so it is not something this actor may be turned away from (#5116).
        if (baselineProposal && !canPersistProposalUpdates && !collaboration.hasPendingClientTeamMembers) {
          enqueueSnackbar("You do not have permission to save updates to this proposal.", {
            variant: "warning",
          });
          return;
        }

        if (collaboration.mode === "canonical") {
          let savedEnvelope = collaboration.envelope;
          let hasCanonicalUpdates = false;
          // Only an actor permitted to persist proposal-content/reviewer changes queues and flushes
          // them. A comment-only reviewer skips this entirely and falls through to publishing comments.
          if (canPersistProposalUpdates) {
            // This re-flatten only catches a final un-queued edit; it must never retract queued local
            // work. `proposalToSave` is rebuilt from refs and can lag the Redux queue after an async
            // collection add (a conflicts document or fee category just added), so without this guard a
            // stale snapshot would emit a spurious CollectionItemRemoved that composes the genuine
            // pending add to null and the save would find nothing to persist.
            const queuedUpdate = collaboration.queueProposalUpdate(proposalToSave, {
              preservePendingCollectionAdds: true,
            });
            hasCanonicalUpdates = collaboration.hasPendingChanges || queuedUpdate;
          }
          // Outside the content gate: the roster is reference state with a capability of its own,
          // so it is saved by this press whether or not this actor may edit the draft (#5116).
          hasCanonicalUpdates = hasCanonicalUpdates || collaboration.hasPendingClientTeamMembers;
          if (hasCanonicalUpdates) {
            savedEnvelope = await collaboration.savePendingChanges();
            if (!savedEnvelope) throw new Error("Canonical proposal save returned no envelope.");
          }

          if (hasCanonicalUpdates) {
            enqueueSnackbar(
              getProposalSaveSuccessMessage({
                confirmationMode,
                hasReviewerOnlyUpdates: hasReviewerContext && !reviewerActionState.hasPendingSaveUpdates,
              }),
              { variant: "success" },
            );

            // S2 (ROADMAP.md): a save is never rejected for a content conflict, so the 200 above can
            // still have left part of what was just saved un-persisted. Surface it — a silent partial
            // success must never look identical to a full one.
            const dropWarning = getProposalOperationDropWarning(savedEnvelope?.operationReceipts);
            if (dropWarning) enqueueSnackbar(dropWarning, { variant: "warning", persist: true });
          }

          return savedEnvelope?.projection.id ?? proposalToSave.id?.value;
        }

        let savedProposalJSON: ProposalJSON | null = null;
        // Create only while this editor has no proposal at all. `proposalToSave.id` alone was the old
        // test, and it is null again on the very next render after a create.
        const persistedProposalId = proposalToSave.id ?? createdProposalRef.current?.id;
        if (!persistedProposalId) {
          savedProposalJSON = await createProposalQuery({
            proposal: proposalToSave,
            session,
            commandId: draftCreationCommandIdRef.current,
          }).unwrap();
          if (savedProposalJSON && session.user) {
            const savedProposal = Proposal.fromJSON({
              json: savedProposalJSON,
              currentUser: session.user,
            });
            if (savedProposal?.id) {
              const persistedBuilder = new ProposalBuilder({
                session,
                startingSpec: savedProposal.spec,
              });
              builderStorageIdRef.current = savedProposal.id;
              proposalRef.current = savedProposal;
              createdProposalRef.current = savedProposal;
              updatedBuilderRef.current = persistedBuilder;
              setCreatedProposalIdAwaitingHydration(savedProposal.id.value);
              dispatch(
                promoteCreatedProposalDraft({
                  proposal: savedProposalJSON,
                  builder: persistedBuilder,
                }),
              );
              proposalToSave = savedProposal;
            }
          }
        } else if (proposalIsModifiedAgainstBaseline(proposalToSave, baselineProposalToSave)) {
          savedProposalJSON = await saveProposalQuery({
            originalProposal: baselineProposalToSave,
            updatedProposal: proposalToSave,
            session,
          }).unwrap();
          if (!savedProposalJSON) {
            console.error("Failed to save proposal changes", saveProposalResult.error);
            const errorMessage = getProposalSaveErrorMessage({
              confirmationMode,
              hasReviewerOnlyUpdates: hasReviewerContext && !reviewerActionState.hasPendingSaveUpdates,
            });
            enqueueSnackbar(errorMessage, {
              variant: "error",
            });
            return null;
          }
          if (savedProposalJSON && session.user) {
            const savedProposal = Proposal.fromJSON({
              json: savedProposalJSON,
              currentUser: session.user,
              options: { withRedline: true },
            });
            if (savedProposal) {
              proposalRef.current = savedProposal;
            }
          }

          const successMessage = getProposalSaveSuccessMessage({
            confirmationMode,
            hasReviewerOnlyUpdates: hasReviewerContext && !reviewerActionState.hasPendingSaveUpdates,
          });
          enqueueSnackbar(successMessage, {
            variant: "success",
          });
        }

        return savedProposalJSON?.id ?? proposalToSave.id?.value;
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saveProposalResult.error],
  );

  /**
   * Saves once, however many times it is asked to.
   *
   * The check-and-set is synchronous — before any state update or await — so a second entry cannot
   * slip in behind React's commit and take the create branch again. It joins the save already running
   * instead, which is what a second "Save & Close" wants anyway: the id the first one is minting.
   */
  const handleSaveChanges = useCallback(
    (args?: {
      proposal?: Proposal;
      proposalBuilder?: ProposalBuilder;
      confirmSave?: boolean;
      confirmationMode?: ConfirmationMode;
    }): Promise<string | null | undefined> => {
      const saveAlreadyRunning = saveInFlightRef.current;
      if (saveAlreadyRunning) return saveAlreadyRunning;

      const save = persistProposalChanges(args).finally(() => {
        saveInFlightRef.current = null;
      });
      saveInFlightRef.current = save;
      return save;
    },
    [persistProposalChanges],
  );

  const handleSaveUpdatesClicked = useCallback(
    async (args?: {
      confirmSave?: boolean;
      confirmationMode?: ConfirmationMode;
    }): Promise<string | null | undefined> => {
      try {
        const savedProposalId = await handleSaveChanges({
          confirmSave: args?.confirmSave ?? true,
          confirmationMode: args?.confirmationMode,
        });

        if (savedProposalId === null) return null;

        if (!savedProposalId && !proposalId) {
          throw new Error("Saved proposal is missing an ID");
        }

        if (savedProposalId && !proposalId && !location.pathname.includes("/proposals/drafts/edit")) {
          navigate(`/proposals/drafts/edit/${savedProposalId}/details`, { replace: true });
          dispatchPanelState({ type: "setSelectedReviewField" });
        }

        return savedProposalId;
      } catch (error) {
        console.error("Error saving proposal updates", error);
        enqueueSnackbar("Error saving proposal updates.", { variant: "error" });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatchPanelState, location.pathname, proposalId],
  );

  /**
   * The marketplace team behind this proposal's vendor team, when there is one.
   *
   * Read once here rather than in the drawer's props, so the fee-schedule panel and anything else
   * that needs it cannot disagree about which team's profile is being offered.
   */
  const marketplaceVendorTeamId = getMarketplaceTeamId(
    updatedBuilder?.currentSpec.workAgreement?.vendorTeam,
  );

  function handleCreateCategory() {
    dispatchPanelState({ type: "openAddFeePanel" });
  }

  function handleEditCategory(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    dispatchPanelState({ type: "openAddFeePanel", category });
  }

  function handleAddExistingCategory() {
    dispatchPanelState({ type: "openExistingFeePanel" });
  }

  function handleExistingCategoriesSelected(
    categories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>,
  ) {
    if (!updatedBuilder) {
      throw new Error("Proposal builder is required to add existing fee categories.");
    }

    const existingCategories = updatedBuilder.currentSpec.workAgreement?.feeSchedule ?? [];

    if (Masked.isMasked(existingCategories) || Masked.isMasked(categories)) {
      enqueueSnackbar("Cannot add existing categories because the current fee schedule is masked.", {
        variant: "error",
      });
      dispatchPanelState({ type: "closeExistingFeePanel" });
      return;
    }

    let updatedCategories = existingCategories;
    const { duplicateCategories, uniqueCategories } = FeeSchedule.splitCategoriesByDuplicate(
      existingCategories,
      categories,
    );

    if (duplicateCategories.length > 0) {
      enqueueSnackbar(
        "Some categories were not added because categories with the same name already exist in the fee schedule.",
        { variant: "warning" },
      );
    }

    updatedCategories = [...existingCategories, ...uniqueCategories];

    dispatchPanelState({ type: "closeExistingFeePanel" });
    handleProposalBuilderUpdated(updatedBuilder.setFeeSchedule(updatedCategories));
  }

  function handleCategoryAdded(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    if (!updatedBuilder) {
      throw new Error("Proposal builder is required to add a fee category.");
    }

    const selectedCategories = updatedBuilder?.currentSpec.workAgreement?.feeSchedule ?? [];

    if (Masked.isMasked(selectedCategories) || Masked.isMasked(category)) {
      enqueueSnackbar("Cannot add category because the fee schedule is masked.", { variant: "error" });
      return;
    }

    if (selectedCategories.some((c) => c.isEqualTo(category))) {
      enqueueSnackbar("Fee category is already in the fee schedule.", { variant: "warning" });
      return;
    }

    const updatedCategories = [
      ...selectedCategories,
      category instanceof FeeScheduleCategoryMaskedCurrency
        ? new FeeScheduleCategoryMaskedCurrency({
            id: Guid.generate(),
            name: category.name,
            description: category.description ?? null,
            billingCode: category.billingCode ?? null,
            teamMember: category.teamMember,
            teamMemberIsMasked: category.teamMemberIsMasked,
          })
        : new FeeScheduleCategory({
            name: category.name,
            description: category.description ?? null,
            fee: category.fee,
            billingCode: category.billingCode ?? null,
            id: Guid.generate(),
            teamMember: category.teamMember,
            teamMemberIsMasked: category.teamMemberIsMasked,
          }),
    ];

    handleProposalBuilderUpdated(updatedBuilder.setFeeSchedule(updatedCategories));
    setNewFeeCategoryFormKey(Guid.generate().value);
    dispatchPanelState({ type: "closeAddFeePanel", clearCategory: true });
  }

  function handleCategoryUpdated(
    originalName: FeeScheduleCategoryName | null,
    updatedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) {
    if (originalName === null) return;

    if (!updatedBuilder) {
      throw new Error("Proposal builder is required to update a fee category.");
    }

    const selectedCategories = updatedBuilder?.currentSpec.workAgreement?.feeSchedule ?? [];

    if (Masked.isMasked(selectedCategories) || Masked.isMasked(updatedCategory)) {
      enqueueSnackbar("Cannot update category because the fee schedule is masked.", { variant: "error" });
      return;
    }

    if (selectedCategories.some((c) => c.isEqualTo(updatedCategory))) {
      enqueueSnackbar("Fee category is already in the fee schedule.", { variant: "warning" });
      return;
    }

    const updatedCategories = selectedCategories.map((category) => {
      if (category.name?.isEqualTo(originalName)) return updatedCategory;
      return category;
    });

    handleProposalBuilderUpdated(updatedBuilder.setFeeSchedule(updatedCategories));
    dispatchPanelState({ type: "closeAddFeePanel" });
  }
  // #endregion

  // #region Render
  const pageTitle = (() => {
    if (pageAction === "view") return "View";
    if (pageAction === "create") return "Create";
    if (pageAction === "review") return "Review Draft";
    if (pageAction === "edit") return "Edit Draft";
    return "View";
  })();
  const currentUserReviewer = reviewers.find((reviewer) => reviewer.userId?.isEqualTo?.(session.user?.id));
  const reviewerActionCustomMessage = isReviewing ? currentUserReviewer?.customMessage?.trim() : undefined;
  // Fields whose only pending item is an unread counterparty change, so the issue traverser stops there.
  const unreadChangeFields = proposalUnreadChangeFields(
    collaboration.mode === "canonical" ? collaboration.envelope : undefined,
    session.user?.id?.value,
  );
  const manageReviewersDisabled =
    !updatedBuilder ||
    pageControlsArePaused ||
    isSelectingClient ||
    isSelectingClient ||
    isManagingReviewers ||
    reviewerSelectorAnchor !== undefined ||
    isReadOnlyView ||
    isReviewing;
  const pendingReviewCount = hasPendingNonReviewerBuilderChanges
    ? reviewers.length
    : reviewers.filter((reviewer) => !reviewerHasCurrentApproval(reviewer)).length;
  // Comments are deliberately absent. The page used to count the actor's staged comments as unsaved
  // work, which armed the discard-on-navigation prompt over a conversation with nothing pending in it;
  // a comment is written when it is written, so leaving the page cannot lose one.
  let isDirty = hasPendingReviewerChanges;
  if (!proposal) {
    isDirty = isDirty || builderDiffersFromOriginal;
  }
  // A proposal whose source could not answer cannot be saved either — the editor is holding a partial
  // picture of it, and the page has no way to tell "empty" from "unread".
  const proposalSourceFailedToLoad = collaboration.mode === "error";
  const disableSaveChanges = !isDirty || !updatedBuilderIssues.canSave || proposalSourceFailedToLoad;
  const disableSave =
    pageControlsArePaused ||
    giveReviewerApprovalQueryResult.isLoading ||
    completeProposalReviewResult.isLoading ||
    isSelectingClient ||
    isManagingReviewers ||
    disableSaveChanges;
  const hasPendingDraftContentUpdates =
    collaboration.mode === "canonical"
      ? collaboration.hasPendingChanges
      : hasPendingNonReviewerBuilderChanges || (!proposal && builderDiffersFromOriginal);
  const saveButtonTooltip = getSaveButtonTooltip({
    hasPendingDraftContentUpdates,
    hasReviewerRecipients: reviewers.length > 0 && !isReviewing,
    isDirty,
    proposalIsSaved: Boolean(proposalId),
    proposalCanSave: updatedBuilderIssues.canSave,
    userCanEdit: Boolean(session.user?.canEdit(proposal)),
  });
  const drawerWidth = getProposalDrawerWidth({
    teamSelectionOpen: isTeamSelectionDrawerOpen,
    commentsOpen: commentsPanelIsOpen && commentsPaneIsAvailable,
    addFeeOpen: isAddFeePanelOpen,
    existingFeeOpen: isExistingFeePanelOpen,
    canSeatSidePanel,
  });
  const hasDraftBuilderToCleanUp = Boolean(
    originalBuilderJSON || updatedBuilderJSON || originalBuilder || updatedBuilder,
  );

  const navigationAwayBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      (isDirty || hasDraftBuilderToCleanUp) &&
      !isSubmittingProposalRef.current &&
      !isDeletingProposalRef.current &&
      isDraftProposalEditorPath(currentLocation.pathname) &&
      nextLocation.pathname !== appDirectedNavigationPathRef.current &&
      !isDraftProposalEditorPath(nextLocation.pathname)
    );
  });
  const handleNavigateBack = useCallback(
    async (continueNavigation: () => void) => {
      if (isDirty) {
        const response = await confirm({
          title: "Discard changes?",
          message: "Are you sure you want to discard changes to the proposal?",
          okButtonText: "Discard",
          alternativeOkText: disableSaveChanges ? undefined : "Save & Close",
          cancelButtonText: "Cancel",
        });

        if (response === ConfirmResponse.Cancel) return false;
        if (response === ConfirmResponse.AltOk) {
          const savedProposalId = await handleSaveUpdatesClicked({ confirmSave: false });
          if (!savedProposalId) return false;
        }
      }

      handleProposalDraftCleanup(proposalId);

      dispatchPanelState({ type: "setSelectedReviewField" });
      continueNavigation();
      return true;
    },
    [
      confirm,
      disableSaveChanges,
      handleProposalDraftCleanup,
      handleSaveUpdatesClicked,
      isDirty,
      dispatchPanelState,
      proposalId,
    ],
  );

  // Read through a ref so a new `handleNavigateBack` identity cannot re-run this effect and cancel a
  // confirm dialog that is already open in front of the user. The blocker instance is the only thing
  // this effect reacts to.
  const handleNavigateBackRef = useRef(handleNavigateBack);
  useEffect(() => {
    handleNavigateBackRef.current = handleNavigateBack;
  });

  // Drives exactly the blocker instance this effect captured, and never one that has already been
  // resolved. `proceed()` asserts a blocked -> proceeding transition inside React Router and throws
  // "Invalid blocker state transition: unblocked -> proceeding" if the router has moved on — which it
  // does for reasons this page does not control. Completing ANY navigation idles every blocker
  // (React Router's `completeNavigation`), a reset idles this one, and unmounting deletes its key
  // outright. The confirm dialog above waits on a human, so it can be open across all of that.
  //
  // The cleanup is the authority on whether the captured blocker is still live: React runs it in the
  // same commit that replaces or deletes the blocker, and on unmount. Reading a ref synced by a
  // separate effect cannot promise that — it lags by a passive-effect flush, and once the page
  // unmounts it never updates again and stays frozen on a blocked object the router has dropped.
  useEffect(() => {
    if (navigationAwayBlocker.state !== "blocked") return;

    let blockerIsLive = true;
    let didNavigate = false;

    void (async () => {
      try {
        didNavigate = await handleNavigateBackRef.current(() => {
          if (blockerIsLive) navigationAwayBlocker.proceed();
        });
      } finally {
        // Proceeding retires the blocker on its own, so a navigation that went ahead is never reset
        // as well. Releasing it in `finally` means a handler that throws cannot strand the router
        // with a blocked navigation nobody will ever answer.
        if (blockerIsLive && !didNavigate) navigationAwayBlocker.reset();
      }
    })();

    return () => {
      blockerIsLive = false;
    };
  }, [navigationAwayBlocker]);

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;
  // Originating a proposal is an entity-wide act — see `includesEntityWideReadWriteAccessToProposals`.
  // Refusing here rather than at Save is the point: every route into a new draft (the Proposals page,
  // the marketplace's Create Proposal, a pasted URL) lands on this component, so this one gate stops
  // anyone building a whole proposal the API was always going to answer 403 to.
  if (!proposalId && !sessionPermissions.includesEntityWideReadWriteAccessToProposals) {
    return (
      <NotAuthorized message="You do not have permission to create a proposal. Ask an administrator at your firm for entity-wide proposal access." />
    );
  }

  // #region Secondary Actions
  /**
   * The actions that manage the draft rather than move it on, in the order they are offered.
   *
   * `ProposalSecondaryActions` draws them as buttons while the pane can seat them and collapses them
   * into one menu once it cannot, so Submit is never below a row of things that could have waited
   * (#5239). The active page describes its own set the same way, which is what keeps the two pages
   * from answering the same question differently.
   *
   * One literal with conditional spreads rather than a `push` per action: the delete handler guards
   * itself with a ref, and handing such a function to any call during render is what
   * `react-hooks/refs` warns about. Nothing here calls anything.
   */
  const secondaryActions: ProposalSecondaryAction[] = [
    ...(!isReadOnlyView && !isReviewing
      ? [
          {
            key: "reviewers",
            label: "Reviewers",
            icon: <GroupsIcon />,
            badge:
              manageReviewersDisabled || !reviewers.length ? undefined : (
                <ReviewerApprovalBadge pendingReviewCount={pendingReviewCount} />
              ),
            loading: isLoading,
            disabled: isLoading || manageReviewersDisabled,
            onClick: handleManageReviewersClicked,
          },
        ]
      : []),
    ...(!isReadOnlyView && proposal?.creator?.userId.isEqualTo(session.user?.id)
      ? [
          {
            key: "delete-proposal",
            label: "Delete Proposal",
            icon: <DeleteIcon />,
            destructive: true,
            loading: isDeleting,
            disabled:
              pageControlsArePaused ||
              isSelectingClient ||
              isDeleting ||
              isSubmitting ||
              isSaving ||
              isManagingReviewers ||
              !proposal?.id ||
              isReviewing,
            onClick: handleDeleteClicked,
          },
        ]
      : []),
  ];

  return (
    <WorkPaneWidthContext.Provider value={paneWidth}>
    <ThemeProvider theme={paneTheme}>
    <PageContainer>
      <Main
        ref={paneRef}
        dimmed={isAddFeePanelOpen || isExistingFeePanelOpen || isTeamSelectionDrawerOpen}
        mobilePadding="compact"
        open={commentsPanelIsOpen || isAddFeePanelOpen || isExistingFeePanelOpen || isTeamSelectionDrawerOpen}
      >
        <StickyPageHeader>
          <HeaderContainer>
            <TitleContainer>
              <PageTitle variant="h4">
                <PageTitleBreadcrumb>
                  <Tooltip title="Back to Proposals" placement="bottom">
                    <PageTitleLink component={RouterLink} to="/proposals/drafts">
                      Proposals
                    </PageTitleLink>
                  </Tooltip>
                  &nbsp;&gt;
                </PageTitleBreadcrumb>
                {pageTitle}
              </PageTitle>
            </TitleContainer>
            <ActionButtonsContainer>
              <IssueTrackerHeader>
                <IssueTraverser
                  disabled={pageControlsArePaused}
                  issueFields={issueFields}
                  unreadChangeFields={unreadChangeFields}
                  currentField={activeReviewField}
                  prevLabel={ISSUE_TRAVERSER_PREV_LABEL}
                  nextLabel={ISSUE_TRAVERSER_NEXT_LABEL}
                  onTraverseToNewField={handleTraverseFromTraverser}
                />
              </IssueTrackerHeader>
              <Tooltip title={saveButtonTooltip}>
                <span>
                  {paneIsNarrow ? (
                    <ButtonContainer>
                      <SaveButton
                        // The visible "Save" caption is the button's sibling, not its content, and the
                        // tooltip above names the wrapper. Without this the control reaches a screen
                        // reader unnamed — the same hole the Comments button below fixed.
                        aria-label="Save"
                        size="medium"
                        color="primary"
                        disabled={disableSave}
                        onClick={() =>
                          handleSaveUpdatesClicked({
                            confirmSave: true,
                            confirmationMode: isReviewing ? "review" : "save",
                          })
                        }
                      >
                        <SaveIcon fontSize="medium" />
                      </SaveButton>
                      <SaveButtonLabel variant="button" color="primary" disabled={disableSave}>
                        Save
                      </SaveButtonLabel>
                    </ButtonContainer>
                  ) : (
                    <LoadingButton
                      variant="contained"
                      size="large"
                      loading={isSaving}
                      color="primary"
                      disabled={disableSave}
                      startIcon={<SaveIcon />}
                      onClick={() =>
                        handleSaveUpdatesClicked({
                          confirmSave: true,
                          confirmationMode: isReviewing ? "review" : "save",
                        })
                      }
                    >
                      Save
                    </LoadingButton>
                  )}
                </span>
              </Tooltip>
              <CommentButton>
                <Tooltip
                  enterDelay={600}
                  placement="bottom-end"
                  title={proposal?.id ? "" : "Save the proposal to add comments."}
                >
                  <ButtonContainer>
                    <HighlightCommentButton
                      // The tooltip is the button's accessible name only while it has one; a saved
                      // proposal suppresses it, and the visible "Comments" label below is a sibling
                      // of the button, not its content. Name the icon button itself so it keeps that
                      // name in both states.
                      aria-label="Comments"
                      active={activeReviewField?.isEqualTo(ProposalField.General)}
                      size="medium"
                      color="primary"
                      disabled={pageControlsArePaused || !proposal?.id}
                      onClick={handleOpenGeneralCommentsPanel}
                    >
                      <Badge
                        variant={unreadCommentCount ? "standard" : "dot"}
                        color="secondary"
                        overlap="circular"
                        badgeContent={unreadCommentCount > 0 ? unreadCommentCount : undefined}
                        invisible={unreadCommentCount === 0}
                      >
                        <CommentIcon fontSize="medium" />
                      </Badge>
                    </HighlightCommentButton>
                    <CommentButtonLabel
                      variant="button"
                      color="primary"
                      disabled={pageControlsArePaused || !proposal?.id}
                    >
                      Comments
                    </CommentButtonLabel>
                  </ButtonContainer>
                </Tooltip>
              </CommentButton>
            </ActionButtonsContainer>
          </HeaderContainer>
          {updatedBuilder && (
            <ProposalTabsHeader compact>
              <TabsContainer
                variant="scrollable"
                scrollButtons="auto"
                indicatorColor="primary"
                textColor="primary"
                allowScrollButtonsMobile
                value={activeTab}
                onChange={handleTabChange}
              >
                <ProposalTab
                  value={PageTab.Details}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Details}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      pendingChangeCount={tabPendingCounts.pendingChangeCount(ProposalFieldCategory.Details)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.ClientTeam}
                  disabled={shouldDisableTabs()}
                  label="Client Team"
                />
                <ProposalTab
                  value={PageTab.VendorTeam}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      title="Vendor Team"
                      issues={issues}
                      category={ProposalFieldCategory.Team}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      pendingChangeCount={tabPendingCounts.pendingChangeCount(ProposalFieldCategory.Team)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.FeeSchedule}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.FeeSchedule}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      pendingChangeCount={tabPendingCounts.pendingChangeCount(ProposalFieldCategory.FeeSchedule)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Conflicts}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Conflicts}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      pendingChangeCount={tabPendingCounts.pendingChangeCount(ProposalFieldCategory.Conflicts)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Policies}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Policies}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      pendingChangeCount={tabPendingCounts.pendingChangeCount(ProposalFieldCategory.Policies)}
                    />
                  }
                />
                <ProposalTab
                  value={PageTab.Discount}
                  disabled={shouldDisableTabs()}
                  label={
                    <ProposalIssuesBadge
                      issues={issues}
                      category={ProposalFieldCategory.Discount}
                      envelope={collaboration.mode === "canonical" ? collaboration.envelope : undefined}
                      currentUserId={session.user?.id?.value}
                      pendingChangeCount={tabPendingCounts.pendingChangeCount(ProposalFieldCategory.Discount)}
                    />
                  }
                />
              </TabsContainer>
            </ProposalTabsHeader>
          )}
          {updatedBuilder && (
            <>
              <PageActionsContainer>
                <ProposalActions>
                  {isReviewing && (
                    <Tooltip enterDelay={600} placement="bottom" title={submitReviewTooltip}>
                      <span>
                        <SubmitReviewButton
                          disabled={
                            pageControlsArePaused ||
                            giveReviewerApprovalQueryResult.isLoading ||
                            completeProposalReviewResult.isLoading ||
                            isSelectingClient ||
                            isManagingReviewers
                          }
                          size={paneIsNarrow ? "small" : "medium"}
                          finalized={hasUserApprovedReview}
                          loading={
                            isSaving || giveReviewerApprovalQueryResult.isLoading || completeProposalReviewResult.isLoading
                          }
                          canEdit={canEditProposal}
                          hasPendingChanges={hasPendingReviewerChanges}
                          pendingChangeSummary={{
                            heading: proposalSaveChangeLogHeading({
                              audienceLabel: reviewCompletionSharePlan.audienceLabel,
                            }),
                            descriptors: reviewCompletionSharePlan.descriptors,
                          }}
                          onFinalize={handleFinalizeReviewClicked}
                          onSubmitAllAndFinalize={handleSubmitAllAndFinalizeReviewClicked}
                        />
                      </span>
                    </Tooltip>
                  )}
                  {reviewerActionCustomMessage && (
                    <ReviewerActionMessage aria-label="Reviewer custom message">
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Message from team leader
                      </Typography>
                      <Tooltip
                        enterDelay={600}
                        placement="bottom"
                        title={
                          <ReviewerActionMessageTooltipTitle>
                            {reviewerActionCustomMessage}
                          </ReviewerActionMessageTooltipTitle>
                        }
                      >
                        <ReviewerActionMessageBody variant="body2">
                          {reviewerActionCustomMessage}
                        </ReviewerActionMessageBody>
                      </Tooltip>
                    </ReviewerActionMessage>
                  )}
                  {!isReadOnlyView && !isReviewing && (
                    <Tooltip
                      enterDelay={600}
                      placement="bottom"
                      title={
                        issues?.canSubmit
                          ? "Submit proposal to other party."
                          : "Fill out required fields to submit."
                      }
                    >
                      <ProposalActionSpan>
                        <ProposalActionButton
                          variant="outlined"
                          color="success"
                          size={paneIsNarrow ? "small" : "medium"}
                          startIcon={issues?.canSubmit ? <SendIcon /> : <WarningIcon />}
                          loading={isSubmitting}
                          disabled={
                            pageControlsArePaused ||
                            isSelectingClient ||
                            isManagingReviewers ||
                            !issues?.canSubmit ||
                            isReadOnlyView ||
                            isReviewing
                          }
                          onClick={handleSubmitClicked}
                        >
                          Submit
                        </ProposalActionButton>
                      </ProposalActionSpan>
                    </Tooltip>
                  )}
                  {
                    // #region Secondary Actions
                  }
                  <ProposalSecondaryActions
                    actions={secondaryActions}
                    collapsed={paneIsNarrow}
                    size={paneIsNarrow ? "small" : "medium"}
                  />
                </ProposalActions>
              </PageActionsContainer>
              {/* A popover, so it is not a cell of the actions grid; it hangs off whichever control
                  opened it — the Reviewers button, or "More Actions" once they have collapsed. */}
              {!isReadOnlyView && !isReviewing && (
                <ReviewerSelector
                  proposal={proposal ?? undefined}
                  popoverAnchor={reviewerSelectorAnchor}
                  reviewerSource="proposalBuilder"
                  reviewerBaseline={reviewerBaseline}
                  canRequestReReview={reReview.canRequestReReview}
                  reReviewPendingUserIds={reReview.pendingUserIds}
                  onRequestReReview={reReview.requestReReview}
                  proposalHasUnsavedChanges={isDirty}
                  isSendingToReviewers={isSaving}
                  // #4842: the header Save is disabled while the popover is open, so the
                  // popover carries its own way to persist the roster; it closes first so
                  // the confirmation is not stacked behind it. #5134: Send is no longer
                  // gated on this page being dirty, so it asks first only when it is —
                  // when it would flush proposal edits the actor did not press Save for.
                  onSendToReviewers={({ confirmSave }) => {
                    dispatchPanelState({ type: "closeReviewerSelector" });
                    void handleSaveUpdatesClicked({ confirmSave, confirmationMode: "save" });
                  }}
                  onPopoverClose={() => {
                    dispatchPanelState({ type: "closeReviewerSelector" });
                  }}
                  onReviewersChanged={(updatedReviewers) => {
                    if (!session.context) throw new Error("Session not properly registered.");
                    const latestBuilder = updatedBuilderRef.current;
                    if (!latestBuilder)
                      throw new Error("Proposal builder not registered properly in store.");
                    void handleProposalBuilderUpdated(
                      session.context?.viewingAsVendor
                        ? latestBuilder.setVendorReviewers(updatedReviewers)
                        : latestBuilder.setClientReviewers(updatedReviewers),
                    );
                  }}
                />
              )}
            </>
          )}
        </StickyPageHeader>
        {!updatedBuilder && <NoBuilderLoader />}
        {updatedBuilder && (
          <Content {...paneFlushContent}>
          {/* Lets the change traverser open a field's history where the field lives — inside a tab. */}
          <ProposalFieldHistoryRequestContext.Provider value={fieldHistoryRequest}>
            <TabContent disableBottomPadding={activeTab === PageTab.FeeSchedule}>
              {updatedBuilder && (
                <>
                    {activeTab === PageTab.Details && (
                      <DetailsTab
                        proposal={proposal ?? undefined}
                        isSaved={Boolean(proposal?.id)}
                        issues={issues}
                        activeReviewField={activeReviewField}
                        proposalBuilder={updatedBuilder}
                        showPendingChangeHighlights={false}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        onSelectingClientChange={(isSelecting: boolean) => setIsSelectingClient(isSelecting)}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      />
                    )}
                    {activeTab === PageTab.ClientTeam && (
                      <ClientTeamTab
                        proposal={proposal ?? undefined}
                        proposalBuilder={updatedBuilder}
                        negotiationId={collaboration.envelope?.negotiationId}
                        canEdit={
                          proposal?.id
                            ? collaboration.envelope?.capabilities.canManageClientTeam
                            : Boolean(
                              !isReadOnlyView &&
                              !pageControlsArePaused &&
                              !reviewCompletionLocksEditing &&
                              canEditProposal,
                            )
                        }
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      />
                    )}
                    {activeTab === PageTab.VendorTeam && (
                      <TeamTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        showPendingChangeHighlights={false}
                        issues={issues}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        canChangeTeamIdentity={canChangeVendorTeamIdentity}
                        isDrawerOpen={commentsPanelIsOpen || isTeamSelectionDrawerOpen}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onSelectionDrawerOpenChange={handleTeamSelectionDrawerOpenChange}
                      />
                    )}
                    {activeTab === PageTab.FeeSchedule && (
                      <FeeScheduleTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        showPendingChangeHighlights={false}
                        issues={issues}
                        isDrawerOpen={isAddFeePanelOpen || isExistingFeePanelOpen || commentsPanelIsOpen}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                        onCreateCategoryClicked={handleCreateCategory}
                        onAddExistingCategoryClicked={handleAddExistingCategory}
                        onEditCategory={handleEditCategory}
                      />
                    )}
                    {activeTab === PageTab.Conflicts && (
                      <ConflictsTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        proposalId={proposal?.id}
                        showPendingChangeHighlights={false}
                        issues={issues}
                        isDrawerOpen={commentsPanelIsOpen}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      />
                    )}
                    {activeTab === PageTab.Policies && (
                      <PoliciesTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        proposalId={proposal?.id}
                        showPendingChangeHighlights={false}
                        issues={issues}
                        isDrawerOpen={commentsPanelIsOpen}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      />
                    )}
                    {activeTab === PageTab.Discount && (
                      <DiscountTab
                        negotiationId={collaboration.envelope?.negotiationId}
                        viewerScope={collaboration.viewerScope}
                        proposalBuilder={updatedBuilder}
                        showPendingChangeHighlights={false}
                        issues={issues}
                        disableEditing={Boolean(
                          isReadOnlyView ||
                          pageControlsArePaused ||
                          reviewCompletionLocksEditing ||
                          !canEditProposal,
                        )}
                        activeReviewField={activeReviewField}
                        onProposalBuilderUpdated={handleProposalBuilderUpdated}
                      />
                    )}
                </>
              )}
            </TabContent>
          </ProposalFieldHistoryRequestContext.Provider>
          </Content>
        )}
        <IssueTrackerFooter>
          <IssueTraverser
            disabled={pageControlsArePaused}
            issueFields={issueFields}
            unreadChangeFields={unreadChangeFields}
            currentField={activeReviewField}
            prevLabel={ISSUE_TRAVERSER_PREV_LABEL}
            nextLabel={ISSUE_TRAVERSER_NEXT_LABEL}
            onTraverseToNewField={handleTraverseFromTraverser}
          />
        </IssueTrackerFooter>
      </Main>
      <SidePanelColumn
        data-testid="proposal-drawers"
        style={{
          width: drawerWidth,
        }}
      >
        {commentsPaneIsAvailable && (
          // Provide the field-navigation traverser to the change-history timeline nested inside the
          // drawer (E13) by context, so the drawer stays a transparent pass-through.
          <ProposalFieldNavigationContext.Provider value={handleTraverseToNewField}>
            <CommentsDrawer
              proposal={commentProposal ?? proposal}
              commentThreads={commentThreads}
              isOpen={commentsPanelIsOpen}
              isSaving={isSaving}
              title={commentsPanelTitle}
              negotiationId={collaboration.envelope?.negotiationId}
              viewerScope={collaboration.viewerScope}
              onOpen={handleOpenCommentsPanel}
              onClose={handleCloseCommentsPanel}
            />
          </ProposalFieldNavigationContext.Provider>
        )}
        <EditFeeScheduleCategoryDrawer
          key={currentCategory?.id.value ?? newFeeCategoryFormKey}
          category={currentCategory}
          formKey={newFeeCategoryFormKey}
          isOpen={isAddFeePanelOpen}
          onOpen={() => dispatchPanelState({ type: "openAddFeePanel", preserveCategory: true })}
          onClose={() => {
            dispatchPanelState({ type: "closeAddFeePanel", clearCategory: true });
          }}
          onCategoryAdded={handleCategoryAdded}
          onCategoryUpdated={handleCategoryUpdated}
          selectedCategories={updatedBuilder?.currentSpec.workAgreement?.feeSchedule ?? []}
          teamMembers={getFeeScheduleTeamMembers(updatedBuilder?.currentSpec.workAgreement?.vendorTeam)}
        />
        <ExistingFeeScheduleCategoryDrawer
          selectedCategories={updatedBuilder?.currentSpec.workAgreement?.feeSchedule ?? []}
          isOpen={isExistingFeePanelOpen}
          onOpen={() => dispatchPanelState({ type: "openExistingFeePanel" })}
          onClose={() => dispatchPanelState({ type: "closeExistingFeePanel" })}
          onCategoriesSelected={handleExistingCategoriesSelected}
          proposalId={negotiationGuid?.toString()}
          // What the vendor team advertises, offered as a second source (#5113). Undefined when the
          // team did not come from the marketplace, or when the team is masked to this viewer.
          marketplaceTeamId={marketplaceVendorTeamId}
          proposalTeamMembers={getFeeScheduleTeamMembers(updatedBuilder?.currentSpec.workAgreement?.vendorTeam)}
        />
        {submitProposalDialog}
      </SidePanelColumn>
    </PageContainer>
    </ThemeProvider>
    </WorkPaneWidthContext.Provider>
  );
}

function proposalFieldCategoryFromPageTab(pageTab: PageTab): ProposalFieldCategoryType {
  switch (pageTab) {
    case PageTab.Details:
      return ProposalFieldCategory.Details;
    case PageTab.VendorTeam:
      return ProposalFieldCategory.Team;
    case PageTab.ClientTeam:
      return ProposalFieldCategory.Details;
    case PageTab.FeeSchedule:
      return ProposalFieldCategory.FeeSchedule;
    case PageTab.Conflicts:
      return ProposalFieldCategory.Conflicts;
    case PageTab.Policies:
      return ProposalFieldCategory.Policies;
    case PageTab.Discount:
      return ProposalFieldCategory.Discount;
  }
}

function pageTabFromProposalFieldCategory(proposalFieldCategory: ProposalFieldCategoryType): PageTab {
  switch (proposalFieldCategory) {
    case ProposalFieldCategory.Details:
      return PageTab.Details;
    case ProposalFieldCategory.Team:
      return PageTab.VendorTeam;
    case ProposalFieldCategory.FeeSchedule:
      return PageTab.FeeSchedule;
    case ProposalFieldCategory.Conflicts:
      return PageTab.Conflicts;
    case ProposalFieldCategory.Policies:
      return PageTab.Policies;
    case ProposalFieldCategory.Discount:
      return PageTab.Discount;
  }
}

function getDraftBuilderForUpdate(args: {
  originalBuilder?: ProposalBuilder | null;
  proposal?: Proposal | null;
  updatedBuilder: ProposalBuilder;
}) {
  const { originalBuilder, proposal, updatedBuilder } = args;

  if (!originalBuilder) return updatedBuilder;
  if (proposal) {
    return proposal.isModifiedByBuilder(updatedBuilder) ? updatedBuilder : originalBuilder;
  }

  return updatedBuilder.isDifferentFrom(originalBuilder) ? updatedBuilder : originalBuilder;
}

function proposalIsModifiedAgainstBaseline(proposal: Proposal, baselineProposal: Proposal): boolean {
  const proposalLike = proposal as Proposal & {
    isModified?: boolean;
    isModifiedAgainst?: (baselineProposal: Proposal) => boolean;
  };

  if (typeof proposalLike.isModifiedAgainst === "function") {
    return proposalLike.isModifiedAgainst(baselineProposal);
  }

  return Boolean(proposalLike.isModified);
}

/**
 * What Save promises, in the author's terms. It used to promise comments too — "Save changes and
 * comments", "Publish your pending comments" — because a save carried them; comments are written as
 * they are made, so Save speaks only for the proposal's own changes.
 */
function getSaveButtonTooltip(args: {
  hasPendingDraftContentUpdates: boolean;
  hasReviewerRecipients: boolean;
  isDirty: boolean;
  proposalCanSave: boolean;
  proposalIsSaved: boolean;
  userCanEdit: boolean;
}) {
  const { hasPendingDraftContentUpdates, hasReviewerRecipients, isDirty, proposalCanSave, proposalIsSaved } =
    args;

  if (!isDirty) return "No changes to save.";

  if (!proposalIsSaved) return "Save proposal.";
  if (!proposalCanSave) return "Fill out all required fields to save.";
  if (!hasPendingDraftContentUpdates) return "Save reviewer changes.";
  if (hasReviewerRecipients) return "Save changes. Reviewers will receive the updates.";

  return "Save changes.";
}

function isDraftProposalEditorPath(pathname: string) {
  return /^\/proposals\/drafts\/(?:create|edit|review|view)(?:\/|$)/.test(pathname);
}
